'use client';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { Credits } from '@/components/Credits';
import { useLocalTranslation } from '@/hooks/useLocalTranslation';
import { useNavigation } from '@/contexts/NavigationContext';
import { useClaimSheetStore } from '@/stores/claimSheetStore';
import { playEarnings } from '@/lib/uiSounds';
import { showErrorToast, showInfoToast } from '@/components/Toast';
import { formatApiError } from '@/utils/apiError';
import { useMyAchievements } from '../hooks/useMyAchievements';
import { useClaimableRewards, useClaimRewards } from '../hooks/useClaimableRewards';
import { pickTitle, useAchievementLang } from '../utils/pickLocale';
import { AchievementIcon } from './AchievementIcon';
import { AchievementClaimBannerView } from './AchievementClaimBanner';
import translations from './AchievementClaimBanner.translations';

/** App-level claim hub. Mounted once per runtime; opened via the claim-sheet
 *  store from the achievements banner, the trophy detail sheet on any screen,
 *  and the unlock toast. */
export function AchievementClaimSheet() {
  const { t } = useLocalTranslation(translations);
  const isOpen = useClaimSheetStore((s) => s.isOpen);
  const close = useClaimSheetStore((s) => s.close);

  return (
    <BottomSheet isOpen={isOpen} onClose={close} title={t('title')} heightClass="h-auto max-h-[85vh]">
      {isOpen && <ClaimSheetBody onClose={close} />}
    </BottomSheet>
  );
}

/** What the claim credited, for the success toast. Same row as the unlock toast:
 *  the coin figure is the `Credits` glyph, XP stays spelled out. */
function ClaimedReward({ coins, xp }: { coins: number; xp: number }) {
  const { t } = useLocalTranslation(translations);
  return (
    <>
      {!!coins && (
        <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-amber-400">
          +<Credits amount={coins} iconSize={11} />
        </span>
      )}
      {!!xp && <span className="text-xs font-semibold text-label-secondary">+{xp} {t('xp')}</span>}
    </>
  );
}

/** Body split out so the achievement queries only run while the sheet is open. */
function ClaimSheetBody({ onClose }: { onClose: () => void }) {
  const { t } = useLocalTranslation(translations);
  const lang = useAchievementLang();
  const { data } = useClaimableRewards();
  const { data: mine } = useMyAchievements();
  const claim = useClaimRewards();
  const navigation = useNavigation();
  const markJustClaimed = useClaimSheetStore((s) => s.markJustClaimed);

  const unclaimed = (mine?.items ?? []).filter((a) => a.unlockedAt && !a.rewardClaimed);

  const onClaim = () => {
    if (claim.isPending) return;
    // Remember what this tap covers before the list is refetched away — the grid
    // replays the settle pop on exactly these tiles, in grid order.
    const keys = unclaimed.map((a) => a.key);
    claim.mutate(undefined, {
      onSuccess: (res) => {
        playEarnings();
        markJustClaimed(keys);
        showInfoToast(t('claimed'), <ClaimedReward coins={res.claimedCoins} xp={res.claimedXp} />);
        onClose();
      },
      onError: (err) => {
        // Sheet stays open on purpose: it's the retry surface. The toast now explains
        // the refusal, so staying put no longer reads as a stuck or silently-successful
        // state — closing would just send the user back to find the same sheet again for
        // the same rewards. The list underneath refetches regardless (see useClaimRewards):
        // if the claim actually went through server-side despite the client-side error,
        // the tiles clear themselves instead of inviting a second, now-invalid tap.
        console.error('Failed to claim achievement rewards:', formatApiError(err));
        showErrorToast(t('claimFailed'), t('claimFailedHint'));
      },
    });
  };

  const onDeposit = () => {
    onClose();
    navigation.navigate('payment');
  };

  return (
    <div className="space-y-4 px-4 pb-6">
      {data && data.count > 0 && (
        <AchievementClaimBannerView
          data={data}
          claiming={claim.isPending}
          onClaim={onClaim}
          onDeposit={onDeposit}
        />
      )}

      {unclaimed.length > 0 && (
        <div className="space-y-1.5">
          {unclaimed.map((a) => (
            <div key={a.key} className="flex items-center gap-3 rounded-xl bg-fill-tertiary/60 p-2">
              <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-lg">
                <AchievementIcon iconFile={a.iconFile} alt="" fill className="object-contain" />
              </div>
              <span className="min-w-0 flex-1 truncate text-sm text-label-primary">
                {pickTitle(a.i18n, lang)}
              </span>
              {!!a.reward.credits && (
                <Credits
                  amount={a.reward.credits}
                  iconSize={11}
                  className="shrink-0 text-xs text-label-secondary"
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
